import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { getWidgetConfig, updateWidgetConfig, toggleWidget, getBillingStatus, type BillingStatus } from '../api';
import { getShopDomain } from '../shopify';

// Form state shape — mirrors the backend config fields exactly
interface SalesPopupConfig {
  frequency: number;
  display_duration: number;
  delay_between: number;
  max_per_session: number;
  position: string;
  bg_color: string;
  text_color: string;
  accent_color: string;
  display_style: string;
  background_style: string;
  gradient_end_color: string;
}

interface StylePreset {
  id: string;
  name: string;
  description: string;
  display_style: string;
  bg_color: string;
  text_color: string;
  accent_color: string;
  background_style?: string;
  gradient_end_color?: string;
}

const STYLE_PRESETS: StylePreset[] = [
  {
    id: 'clean-white',
    name: 'Clean White',
    description: 'White card with dark text',
    display_style: 'card',
    bg_color: '#ffffff',
    text_color: '#333333',
    accent_color: '#c33600',
  },
  {
    id: 'dark-mode',
    name: 'Dark Mode',
    description: 'Dark card with light text',
    display_style: 'card',
    bg_color: '#1a1a1a',
    text_color: '#f5f5f5',
    accent_color: '#ff6b35',
  },
  {
    id: 'subtle-glass',
    name: 'Subtle Glass',
    description: 'Glassmorphism with soft blur',
    display_style: 'card',
    bg_color: '#ffffff',
    text_color: '#333333',
    accent_color: '#005bd3',
    background_style: 'glass',
  },
  {
    id: 'compact-dark',
    name: 'Compact Dark',
    description: 'Minimal footprint, dark theme',
    display_style: 'compact',
    bg_color: '#111111',
    text_color: '#e5e5e5',
    accent_color: '#22c55e',
  },
  {
    id: 'minimal-light',
    name: 'Minimal Light',
    description: 'Smallest footprint, light theme',
    display_style: 'minimal',
    bg_color: '#f8f8f8',
    text_color: '#444444',
    accent_color: '#dc2626',
  },
];

const DEFAULTS: SalesPopupConfig = {
  frequency: 30,
  display_duration: 6,
  delay_between: 5,
  max_per_session: 5,
  position: 'bottom-left',
  bg_color: '#ffffff',
  text_color: '#333333',
  accent_color: '#c33600',
  display_style: 'card',
  background_style: 'solid',
  gradient_end_color: '#e0e0e0',
};

type PreviewState = 'card' | 'compact' | 'minimal';

@customElement('hype-sales-popup-page')
export class SalesPopupPage extends LitElement {
  override createRenderRoot() { return this; }

  // Loading / saving states
  @state() private _loading = true;
  @state() private _saving = false;
  @state() private _loadError = '';
  @state() private _saveSuccess = false;
  @state() private _saveError = '';
  @state() private _saveFlash = false;

  private _flashTimer: ReturnType<typeof setTimeout> | undefined;

  // Widget enabled toggle
  @state() private _enabled = false;

  // Preview state override — does not affect saved config
  @state() private _previewState: PreviewState = 'card';

  // Form fields
  @state() private _frequency = DEFAULTS.frequency;
  @state() private _display_duration = DEFAULTS.display_duration;
  @state() private _delay_between = DEFAULTS.delay_between;
  @state() private _max_per_session = DEFAULTS.max_per_session;
  @state() private _position = DEFAULTS.position;
  @state() private _bg_color = DEFAULTS.bg_color;
  @state() private _text_color = DEFAULTS.text_color;
  @state() private _accent_color = DEFAULTS.accent_color;
  @state() private _display_style = DEFAULTS.display_style;
  @state() private _background_style = DEFAULTS.background_style;
  @state() private _gradient_end_color = DEFAULTS.gradient_end_color;

  // Custom CSS (Pro plan)
  @state() private _custom_css = '';
  @state() private _billing: BillingStatus | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    void this._loadConfig();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    clearTimeout(this._flashTimer);
  }

  private async _loadConfig(): Promise<void> {
    this._loading = true;
    this._loadError = '';
    try {
      const [widget, billing] = await Promise.all([
        getWidgetConfig('sales_popup'),
        getBillingStatus(),
      ]);
      this._billing = billing;
      this._enabled = widget.enabled;
      this._custom_css = widget.custom_css ?? '';
      const c = widget.config as Partial<SalesPopupConfig>;
      this._frequency          = Number(c.frequency          ?? DEFAULTS.frequency);
      this._display_duration   = Number(c.display_duration   ?? DEFAULTS.display_duration);
      this._delay_between      = Number(c.delay_between      ?? DEFAULTS.delay_between);
      this._max_per_session    = Number(c.max_per_session    ?? DEFAULTS.max_per_session);
      this._position           = (c.position           ?? DEFAULTS.position)           as string;
      this._bg_color           = (c.bg_color           ?? DEFAULTS.bg_color)           as string;
      this._text_color         = (c.text_color         ?? DEFAULTS.text_color)         as string;
      this._accent_color       = (c.accent_color       ?? DEFAULTS.accent_color)       as string;
      this._display_style      = (c.display_style      ?? DEFAULTS.display_style)      as string;
      this._background_style   = (c.background_style   ?? DEFAULTS.background_style)   as string;
      this._gradient_end_color = (c.gradient_end_color ?? DEFAULTS.gradient_end_color) as string;
    } catch (err) {
      this._loadError = err instanceof Error ? err.message : 'Failed to load configuration.';
    } finally {
      this._loading = false;
    }
  }

  private async _handleToggle(enabled: boolean): Promise<void> {
    this._enabled = enabled;
    try {
      const config: Record<string, unknown> = {
        frequency: this._frequency,
        display_duration: this._display_duration,
        delay_between: this._delay_between,
        max_per_session: this._max_per_session,
        position: this._position,
        bg_color: this._bg_color,
        text_color: this._text_color,
        accent_color: this._accent_color,
        display_style: this._display_style,
        background_style: this._background_style,
        gradient_end_color: this._gradient_end_color,
      };
      await toggleWidget('sales_popup', enabled, config);
    } catch (err) {
      // Revert optimistic update on failure
      this._enabled = !enabled;
      this._saveError = err instanceof Error ? err.message : 'Failed to toggle widget.';
      this._saveSuccess = false;
    }
  }

  private async _handleSave(): Promise<void> {
    this._saving = true;
    this._saveSuccess = false;
    this._saveError = '';
    try {
      const config: SalesPopupConfig = {
        frequency: this._frequency,
        display_duration: this._display_duration,
        delay_between: this._delay_between,
        max_per_session: this._max_per_session,
        position: this._position,
        bg_color: this._bg_color,
        text_color: this._text_color,
        accent_color: this._accent_color,
        display_style: this._display_style,
        background_style: this._background_style,
        gradient_end_color: this._gradient_end_color,
      };
      await updateWidgetConfig('sales_popup', config as unknown as Record<string, unknown>, this._custom_css);
      this._saveSuccess = true;
      // Trigger brief success flash on save button + preview pulse
      clearTimeout(this._flashTimer);
      this._saveFlash = true;
      this._flashTimer = setTimeout(() => { this._saveFlash = false; }, 1500);
    } catch (err) {
      this._saveError = err instanceof Error ? err.message : 'Failed to save configuration.';
    } finally {
      this._saving = false;
    }
  }

  private _applyPreset(preset: StylePreset): void {
    this._display_style = preset.display_style;
    this._bg_color = preset.bg_color;
    this._text_color = preset.text_color;
    this._accent_color = preset.accent_color;
    this._background_style = preset.background_style ?? 'solid';
    this._gradient_end_color = preset.gradient_end_color ?? '#e0e0e0';
  }

  private _handleColor(key: string, value: string): void {
    if (key === 'bg_color')           this._bg_color           = value;
    if (key === 'text_color')         this._text_color         = value;
    if (key === 'accent_color')       this._accent_color       = value;
    if (key === 'gradient_end_color') this._gradient_end_color = value;
  }

  private _handleRange(field: string, value: number): void {
    if (field === 'frequency')        this._frequency        = value;
    if (field === 'display_duration') this._display_duration = value;
    if (field === 'delay_between')    this._delay_between    = value;
    if (field === 'max_per_session')  this._max_per_session  = value;
  }

  /** Returns a static HTML preview that mimics the widget's card structure. */
  private _renderPreview() {
    const displayStyle = this._previewState;
    const bgStyle = this._background_style;

    const showThumb = displayStyle === 'card';
    const showMeta = displayStyle !== 'minimal';

    let containerMaxW = '280px';
    let containerPad = '0.75em 1em';
    let containerRadius = '0.5em';
    if (displayStyle === 'compact') { containerMaxW = '240px'; containerPad = '0.5em 0.75em'; }
    if (displayStyle === 'minimal') { containerMaxW = '220px'; containerPad = '0.4em 0.75em'; containerRadius = '0.35em'; }

    let bgCss = this._bg_color;
    if (bgStyle === 'gradient') bgCss = `linear-gradient(135deg, ${this._bg_color}, ${this._gradient_end_color})`;

    const borderCss = bgStyle === 'glass' ? '1px solid rgba(255,255,255,0.18)' : 'none';
    const backdropCss = bgStyle === 'glass' ? 'blur(16px)' : 'none';

    return html`
      <div style="display:flex;align-items:center;gap:${displayStyle === 'card' ? '0.75em' : '0.5em'};padding:${containerPad};border-radius:${containerRadius};box-shadow:0 4px 16px rgba(0,0,0,0.15);max-width:${containerMaxW};background:${bgCss};color:${this._text_color};border:${borderCss};backdrop-filter:${backdropCss};font-size:16px;position:relative;">
        <button style="position:absolute;top:0.35em;right:0.35em;background:none;border:none;font-size:0.75em;padding:0.15em 0.3em;color:${this._text_color};opacity:0.5;cursor:pointer;">&#x2715;</button>
        ${showThumb ? html`
          <div style="width:48px;height:48px;border-radius:0.25em;background:#eee;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:1.5em;">&#x1f6cd;&#xfe0f;</div>
        ` : ''}
        <div style="min-width:0;">
          <div style="font-weight:600;font-size:${displayStyle === 'minimal' ? '0.75em' : displayStyle === 'compact' ? '0.8em' : '0.85em'};color:${this._accent_color};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">Premium T-Shirt</div>
          ${showMeta ? html`<div style="font-size:${displayStyle === 'compact' ? '0.7em' : '0.75em'};opacity:0.75;margin-top:0.15em;">Sarah from Melbourne</div>` : ''}
          <div style="font-size:${displayStyle === 'minimal' ? '0.6em' : displayStyle === 'compact' ? '0.65em' : '0.7em'};opacity:0.55;margin-top:0.1em;">3m ago</div>
        </div>
      </div>
    `;
  }

  override render() {
    if (this._loading) {
      return html`<s-spinner></s-spinner>`;
    }

    if (this._loadError) {
      return html`
        <s-page heading="Sales Popup">
          <s-banner tone="critical">
            <strong>Failed to load configuration</strong><br />
            ${this._loadError}
          </s-banner>
          <s-button @click=${() => void this._loadConfig()}>Retry</s-button>
        </s-page>
      `;
    }

    return html`
      <style>
        hype-sales-popup-page .preview-layout {
          display: grid;
          grid-template-columns: 55fr 45fr;
          gap: 24px;
          align-items: start;
        }
        hype-sales-popup-page .config-column {
          display: flex;
          flex-direction: column;
          gap: var(--p-space-base, 16px);
        }
        hype-sales-popup-page .preview-panel {
          position: sticky;
          top: 80px;
        }
        hype-sales-popup-page .preview-frame {
          border: 1px solid #e3e3e3;
          border-radius: 12px;
          background: #fafafa;
          padding: 24px;
          overflow: hidden;
        }
        hype-sales-popup-page .save-btn-success {
          background-color: #008060 !important;
          border-color: #008060 !important;
          transition: background-color 0.2s, border-color 0.2s;
        }
        @keyframes hype-preview-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(0,128,96,0.5); border-color: #008060; }
          60%  { box-shadow: 0 0 0 6px rgba(0,128,96,0); border-color: #008060; }
          100% { box-shadow: 0 0 0 0 rgba(0,128,96,0); border-color: #e3e3e3; }
        }
        hype-sales-popup-page .preview-frame.preview-pulse {
          animation: hype-preview-pulse 1s ease-out forwards;
        }
        @media (prefers-reduced-motion: reduce) {
          hype-sales-popup-page .save-btn-success {
            transition: none;
          }
          hype-sales-popup-page .preview-frame.preview-pulse {
            animation: none;
            border-color: #008060;
          }
        }
        hype-sales-popup-page .preview-states {
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
          margin-bottom: 8px;
        }
        hype-sales-popup-page .preview-state-btn {
          padding: 4px 8px;
          font-size: 11px;
          border: 1px solid #ccc;
          border-radius: 4px;
          background: #fff;
          cursor: pointer;
        }
        hype-sales-popup-page .preview-state-btn.active {
          background: #005bd3;
          color: #fff;
          border-color: #005bd3;
        }
        hype-sales-popup-page .preset-grid {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          margin-bottom: 4px;
        }
        hype-sales-popup-page .preset-card {
          width: 120px;
          cursor: pointer;
          border: 2px solid #e3e3e3;
          border-radius: 8px;
          padding: 10px;
          background: #ffffff;
          display: flex;
          flex-direction: column;
          gap: 6px;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        hype-sales-popup-page .preset-card:hover {
          border-color: #b5b5b5;
          box-shadow: 0 2px 6px rgba(0,0,0,0.10);
        }
        hype-sales-popup-page .preset-card.active {
          border-color: #005bd3;
          box-shadow: 0 0 0 1px #005bd3;
          background: #f0f7ff;
        }
        hype-sales-popup-page .preset-card-name {
          font-size: 12px;
          font-weight: 600;
          color: #303030;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        hype-sales-popup-page .preset-swatches {
          display: flex;
          gap: 3px;
          justify-content: center;
          margin-bottom: 3px;
        }
        hype-sales-popup-page .preset-swatch {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          border: 1px solid rgba(0,0,0,0.15);
        }
        hype-sales-popup-page .preset-card-style {
          font-size: 10px;
          color: #666;
          text-transform: capitalize;
        }
        @media (max-width: 768px) {
          hype-sales-popup-page .preview-layout {
            grid-template-columns: 1fr;
          }
          hype-sales-popup-page .preview-panel {
            position: static;
            order: -1;
          }
        }
      </style>
      <s-page heading="Sales Popup">
        <s-button
          slot="primary-action"
          variant="primary"
          class="${this._saveFlash ? 'save-btn-success' : ''}"
          ?loading=${this._saving}
          @click=${() => void this._handleSave()}
        >${this._saveFlash ? '✓ Saved' : 'Save'}</s-button>
        ${(() => { const shop = getShopDomain(); return shop ? html`<s-button slot="secondary-actions" variant="tertiary" @click=${() => window.open(`https://${shop}`, '_blank')} aria-label="Preview widget on your live store">Preview on store</s-button>` : ''; })()}

        ${this._saveSuccess ? html`
          <s-banner
            tone="success"
            dismissible
            @dismiss=${() => { this._saveSuccess = false; }}
          >Configuration saved successfully.</s-banner>
        ` : ''}

        ${this._saveError ? html`
          <s-banner
            tone="critical"
            dismissible
            @dismiss=${() => { this._saveError = ''; }}
          >${this._saveError}</s-banner>
        ` : ''}

        <s-section heading="Enable">
          <s-checkbox
            label="Enable widget"
            ?checked=${this._enabled}
            @change=${(e: Event) => void this._handleToggle((e.currentTarget as HTMLInputElement).checked)}
          ></s-checkbox>
        </s-section>

        <div class="preview-layout">
          <div class="config-column">
            <s-section heading="Quick Start" class="${this._enabled ? '' : 'disabled-overlay'}">
              <div class="preset-grid">
                ${STYLE_PRESETS.map(preset => {
                  const isActive =
                    preset.display_style === this._display_style &&
                    preset.bg_color === this._bg_color &&
                    preset.text_color === this._text_color &&
                    preset.accent_color === this._accent_color &&
                    (preset.background_style ?? 'solid') === this._background_style &&
                    (preset.gradient_end_color ?? '#e0e0e0') === this._gradient_end_color;
                  return html`
                    <div
                      class="preset-card ${isActive ? 'active' : ''}"
                      role="button"
                      tabindex="0"
                      @click=${() => this._applyPreset(preset)}
                      @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._applyPreset(preset); } }}
                      title=${preset.description}
                    >
                      <div class="preset-card-name">${preset.name}</div>
                      <div class="preset-swatches">
                        <div class="preset-swatch" style="background:${preset.bg_color}"></div>
                        <div class="preset-swatch" style="background:${preset.text_color}"></div>
                        <div class="preset-swatch" style="background:${preset.accent_color}"></div>
                      </div>
                      <div class="preset-card-style">${preset.display_style}</div>
                    </div>
                  `;
                })}
              </div>
            </s-section>

            <s-section heading="Timing" class="${this._enabled ? '' : 'disabled-overlay'}">
              <s-stack gap="base">
                <s-text-field
                  type="number"
                  label="Frequency — show every N seconds"
                  help-text="How often a popup can appear."
                  min="5"
                  max="120"
                  step="5"
                  value=${String(this._frequency)}
                  @change=${(e: Event) => this._handleRange('frequency', Number((e.currentTarget as HTMLInputElement).value))}
                ></s-text-field>

                <s-text-field
                  type="number"
                  label="Display duration"
                  help-text="How long each popup stays visible (seconds)."
                  min="3"
                  max="15"
                  step="1"
                  value=${String(this._display_duration)}
                  @change=${(e: Event) => this._handleRange('display_duration', Number((e.currentTarget as HTMLInputElement).value))}
                ></s-text-field>

                <s-text-field
                  type="number"
                  label="Delay between popups"
                  help-text="Minimum gap between consecutive popups (seconds)."
                  min="0"
                  max="30"
                  step="5"
                  value=${String(this._delay_between)}
                  @change=${(e: Event) => this._handleRange('delay_between', Number((e.currentTarget as HTMLInputElement).value))}
                ></s-text-field>

                <s-text-field
                  type="number"
                  label="Max popups per session"
                  help-text="Cap on total popups shown per visitor session."
                  min="1"
                  max="20"
                  step="1"
                  value=${String(this._max_per_session)}
                  @change=${(e: Event) => this._handleRange('max_per_session', Number((e.currentTarget as HTMLInputElement).value))}
                ></s-text-field>
              </s-stack>
            </s-section>

            <s-section heading="Display" class="${this._enabled ? '' : 'disabled-overlay'}">
              <s-stack gap="base">
                <s-select
                  label="Display style"
                  value=${this._display_style}
                  @change=${(e: Event) => {
                    const val = (e.currentTarget as HTMLInputElement).value;
                    this._display_style = val;
                    this._previewState = val as PreviewState;
                  }}
                >
                  <s-option value="card">Card</s-option>
                  <s-option value="compact">Compact</s-option>
                  <s-option value="minimal">Minimal</s-option>
                </s-select>

                <s-select
                  label="Position"
                  value=${this._position}
                  @change=${(e: Event) => { this._position = (e.currentTarget as HTMLInputElement).value; }}
                >
                  <s-option value="bottom-left">Bottom left</s-option>
                  <s-option value="bottom-right">Bottom right</s-option>
                  <s-option value="top-left">Top left</s-option>
                  <s-option value="top-right">Top right</s-option>
                </s-select>
              </s-stack>
            </s-section>

            <s-section heading="Appearance" class="${this._enabled ? '' : 'disabled-overlay'}">
              <s-stack gap="base">
                <s-select
                  label="Background style"
                  value=${this._background_style}
                  @change=${(e: Event) => { this._background_style = (e.currentTarget as HTMLInputElement).value; }}
                >
                  <s-option value="solid">Solid</s-option>
                  <s-option value="gradient">Gradient</s-option>
                  <s-option value="glass">Glassmorphism</s-option>
                </s-select>

                <s-stack gap="base">
                  <s-text>${this._background_style === 'gradient' ? 'Gradient start color' : 'Background'}</s-text>
                  <s-color-picker
                    value=${this._bg_color}
                    @input=${(e: Event) => this._handleColor('bg_color', (e.currentTarget as HTMLInputElement).value)}
                  ></s-color-picker>
                </s-stack>

                ${this._background_style === 'gradient' ? html`
                  <s-stack gap="base">
                    <s-text>Gradient end color</s-text>
                    <s-color-picker
                      value=${this._gradient_end_color}
                      @input=${(e: Event) => this._handleColor('gradient_end_color', (e.currentTarget as HTMLInputElement).value)}
                    ></s-color-picker>
                  </s-stack>
                ` : ''}

                <s-stack gap="base">
                  <s-text>Text</s-text>
                  <s-color-picker
                    value=${this._text_color}
                    @input=${(e: Event) => this._handleColor('text_color', (e.currentTarget as HTMLInputElement).value)}
                  ></s-color-picker>
                </s-stack>
                <s-stack gap="base">
                  <s-text>Accent</s-text>
                  <s-color-picker
                    value=${this._accent_color}
                    @input=${(e: Event) => this._handleColor('accent_color', (e.currentTarget as HTMLInputElement).value)}
                  ></s-color-picker>
                </s-stack>
              </s-stack>
            </s-section>

            <s-section heading="Custom CSS" class="${this._enabled ? '' : 'disabled-overlay'}">
              <s-stack gap="base">
                ${this._billing && !(this._billing.features as Record<string, boolean>).custom_css ? html`
                  <s-banner tone="info">
                    Custom CSS is available on the Pro plan.
                    <a href="/billing">Upgrade to Pro</a> to inject custom styles.
                  </s-banner>
                ` : ''}
                <textarea
                  rows="6"
                  placeholder=".popup { border-radius: 8px; }"
                  .value=${this._custom_css}
                  ?disabled=${!!(this._billing && !(this._billing.features as Record<string, boolean>).custom_css)}
                  @input=${(e: Event) => { this._custom_css = (e.currentTarget as HTMLTextAreaElement).value; }}
                  style="width:100%;font-family:monospace;font-size:13px;padding:8px;border:1px solid #d2d5d8;border-radius:8px;resize:vertical;box-sizing:border-box;"
                ></textarea>
                <s-text tone="subdued" variant="bodySm">
                  CSS is scoped to this widget's Shadow DOM. Use class names like .popup, .product-name, .meta.
                </s-text>
              </s-stack>
            </s-section>
          </div>

          <div class="preview-panel">
            <s-section heading="Preview">
              <div class="preview-states">
                ${(['card', 'compact', 'minimal'] as PreviewState[]).map(ps => html`
                  <button
                    class="preview-state-btn ${this._previewState === ps ? 'active' : ''}"
                    @click=${() => { this._previewState = ps; }}
                  >${ps.charAt(0).toUpperCase() + ps.slice(1)}</button>
                `)}
              </div>
              <div class="preview-frame ${this._saveFlash ? 'preview-pulse' : ''}">
                ${this._renderPreview()}
              </div>
            </s-section>
          </div>
        </div>
      </s-page>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'hype-sales-popup-page': SalesPopupPage;
  }
}
